
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getUserPrompts } from "@/services/promptCrudService";
import { UserProfile, ProfileFormValues } from "@/components/profile/types";
import type { Prompt } from "@/services/promptService";
import LoadingDebugger from "@/components/debugger/LoadingDebugger";

export const useProfile = (id: string | undefined, isOwnProfile: boolean) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [error, setError] = useState<Error | null>(null);
  
  const form = useForm<ProfileFormValues>({
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      bio: "",
      twitter_url: "",
      linkedin_url: "",
      website_url: ""
    }
  });
  
  const fetchUserData = async () => {
    if (!id) {
      console.error("Cannot fetch profile: ID is undefined");
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching profile data for user: ${id}`);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      
      if (data) {
        console.log(`Profile found for user ${id}:`, data);
        
        // Create a UserProfile object with default values for missing properties
        const profileData: UserProfile = {
          id: data.id,
          first_name: data.first_name,
          last_name: data.last_name,
          username: data.username || null,
          email: data.email || null,
          bio: data.bio || null,
          twitter_url: data.twitter_url || null,
          linkedin_url: data.linkedin_url || null,
          website_url: data.website_url || null,
          created_at: data.created_at,
          updated_at: data.updated_at,
          avatar_url: data.avatar_url,
          role: data.role
        };
        
        setProfile(profileData);
        
        // Initialize form values if it's the user's own profile
        if (isOwnProfile) {
          form.reset({
            username: profileData.username || "",
            first_name: profileData.first_name || "",
            last_name: profileData.last_name || "",
            bio: profileData.bio || "",
            twitter_url: profileData.twitter_url || "",
            linkedin_url: profileData.linkedin_url || "",
            website_url: profileData.website_url || ""
          });
        }
      } else {
        console.error(`No profile found for user ID: ${id}`);
      }
      
      // Fetch user's prompts
      console.log(`Fetching prompts for user: ${id}`);
      const userPrompts = await getUserPrompts(id);
      console.log(`Found ${userPrompts.length} prompts for user ${id}`);
      setPrompts(userPrompts);
      
    } catch (error: any) {
      console.error("Error in useProfile hook:", error);
      setError(error);
      toast.error("Failed to load profile");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUserData();
  }, [id, isOwnProfile, form]);
  
  const handleUpdateProfile = async (values: ProfileFormValues) => {
    try {
      if (!profile?.id) {
        throw new Error("Cannot update profile: No profile ID");
      }
      
      // Use the direct update method
      const { error } = await supabase
        .from('profiles')
        .update({
          username: values.username,
          first_name: values.first_name,
          last_name: values.last_name,
          bio: values.bio,
          twitter_url: values.twitter_url,
          linkedin_url: values.linkedin_url,
          website_url: values.website_url
        })
        .eq('id', profile.id);
      
      if (error) throw error;
      
      // Update local state
      setProfile(prev => {
        if (!prev) return null;
        return {
          ...prev,
          username: values.username,
          first_name: values.first_name,
          last_name: values.last_name,
          bio: values.bio,
          twitter_url: values.twitter_url,
          linkedin_url: values.linkedin_url,
          website_url: values.website_url
        };
      });
      
      setIsEditing(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };
  
  const refreshProfile = () => {
    console.log("Refreshing profile data");
    fetchUserData();
  };
  
  return {
    profile,
    isLoading,
    isEditing,
    setIsEditing,
    prompts,
    form,
    handleUpdateProfile,
    refreshProfile,
    error
  };
};
